/**
 * Webhooks editor — /settings/custom-modules/$key/webhooks
 *
 * List + create + delete + test + view delivery history for webhooks
 * scoped to this module. Create returns a one-time secret which we
 * surface in a "copy this now" banner.
 *
 * Intentionally simple: no edit (except enable/disable toggle, delete,
 * and rotate secret). The form is a single dialog. Delivery history
 * is a collapsible section per webhook.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertCircle,
  Check,
  Copy,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  Webhook,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/webhooks')({
  component: WebhooksPage,
})

function WebhooksPage() {
  const { key: moduleKey } = Route.useParams()
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [newSecret, setNewSecret] = useState<{ id: string; secret: string } | null>(null)

  const { data: webhooks, isLoading } = useQuery(
    orpc.modules.custom.webhooks.list.queryOptions({ input: { moduleKey } }),
  )

  const deleteMutation = useMutation(
    orpc.modules.custom.webhooks.delete.mutationOptions({
      onSuccess: () => {
        toast.success('Webhook deleted')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.webhooks.list.queryKey({
            input: { moduleKey },
          }),
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  const testMutation = useMutation(
    orpc.modules.custom.webhooks.test.mutationOptions({
      onSuccess: () => toast.success('Test delivery enqueued'),
      onError: (err) => toast.error(err.message),
    }),
  )

  const rotateMutation = useMutation(
    orpc.modules.custom.webhooks.update.mutationOptions({
      onSuccess: (webhook) => {
        if (webhook.secret) {
          setNewSecret({ id: webhook.id, secret: webhook.secret })
        }
        toast.success('Secret rotated')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.webhooks.list.queryKey({
            input: { moduleKey },
          }),
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Webhooks</h2>
          <p className="text-sm text-muted-foreground">
            Reusable outbound destinations. Reference by ID from workflow webhook steps.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New webhook
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !webhooks || webhooks.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Webhook className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No webhooks yet. Create one to use from workflow webhook steps.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {webhooks.map((wh) => (
            <Card key={wh.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-base flex items-center gap-2">
                    {wh.name}
                    {wh.enabled ? (
                      <Badge variant="default" className="text-xs">
                        Enabled
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        Disabled
                      </Badge>
                    )}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground font-mono truncate mt-1">
                    {wh.url}
                  </p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <CopyableId id={wh.id} />
                    <span>secret: {wh.secretPreview}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => testMutation.mutate({ id: wh.id })}
                    disabled={testMutation.isPending}
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Test
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      rotateMutation.mutate({ id: wh.id, rotateSecret: true })
                    }
                    disabled={rotateMutation.isPending}
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Rotate
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteMutation.mutate({ id: wh.id })}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => setExpandedId(expandedId === wh.id ? null : wh.id)}
                >
                  {expandedId === wh.id ? 'Hide' : 'Show'} delivery history
                </button>
                {expandedId === wh.id && (
                  <DeliveryHistory webhookId={wh.id} />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateWebhookDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        moduleKey={moduleKey}
        onCreated={(created) => {
          setNewSecret({ id: created.id, secret: created.secret })
          setCreateOpen(false)
        }}
      />

      {newSecret && (
        <SecretBanner
          secret={newSecret.secret}
          onDismiss={() => setNewSecret(null)}
        />
      )}
    </div>
  )
}

// ---------- Copyable webhook ID ----------

function CopyableId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    void navigator.clipboard.writeText(id).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1 font-mono hover:text-foreground"
    >
      <span className="truncate max-w-[12rem]">id: {id.slice(0, 8)}…</span>
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  )
}

// ---------- One-time secret banner ----------

function SecretBanner({ secret, onDismiss }: { secret: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    void navigator.clipboard.writeText(secret).then(() => setCopied(true))
  }
  return (
    <div className="fixed bottom-4 right-4 max-w-md rounded-md border border-amber-500/50 bg-amber-50 dark:bg-amber-950 p-4 shadow-lg">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm">Copy this secret now</p>
          <p className="text-xs text-muted-foreground mb-2">
            It won't be shown again. Use it to verify incoming webhook signatures.
          </p>
          <code className="block font-mono text-xs bg-background rounded border px-2 py-1 break-all">
            {secret}
          </code>
          <div className="flex gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={handleCopy}>
              <Copy className="h-3 w-3 mr-1" />
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              <X className="h-3 w-3 mr-1" />
              Dismiss
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------- Delivery history ----------

function DeliveryHistory({ webhookId }: { webhookId: string }) {
  const { data, isLoading } = useQuery(
    orpc.modules.custom.webhooks.listDeliveries.queryOptions({
      input: { webhookId, page: 1, pageSize: 20 },
    }),
  )

  if (isLoading) return <Skeleton className="h-24 w-full mt-2" />
  if (!data || data.deliveries.length === 0) {
    return (
      <p className="text-xs text-muted-foreground mt-2">No deliveries yet.</p>
    )
  }

  return (
    <ul className="mt-2 divide-y border-t">
      {data.deliveries.map((d) => (
        <li key={d.id} className="py-2 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs">
              <Badge
                variant={
                  d.status === 'success'
                    ? 'default'
                    : d.status === 'failed'
                      ? 'destructive'
                      : 'secondary'
                }
                className="text-xs"
              >
                {d.status}
                {d.responseCode ? ` ${d.responseCode}` : ''}
              </Badge>
              <span className="font-mono">{d.event}</span>
              <span className="text-muted-foreground">
                {new Date(d.createdAt).toLocaleString()}
              </span>
              <span className="text-muted-foreground">#{d.attempts}</span>
            </div>
            {d.responseBody && (
              <p className="text-xs text-muted-foreground mt-1 truncate max-w-xl font-mono">
                {d.responseBody}
              </p>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}

// ---------- Create dialog ----------

function CreateWebhookDialog({
  open,
  onClose,
  moduleKey,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  moduleKey: string
  onCreated: (created: { id: string; secret: string }) => void
}) {
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const queryClient = useQueryClient()

  const createMutation = useMutation(
    orpc.modules.custom.webhooks.create.mutationOptions({
      onSuccess: (webhook) => {
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.webhooks.list.queryKey({
            input: { moduleKey },
          }),
        })
        onCreated({ id: webhook.id, secret: webhook.secret })
        setName('')
        setUrl('')
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New webhook</DialogTitle>
          <DialogDescription>
            The URL is validated against an SSRF block-list. A signing secret is generated
            and shown ONCE in the confirmation banner.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="wh-name">Name</Label>
            <Input
              id="wh-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="QA Slack alerts"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="wh-url">URL</Label>
            <Input
              id="wh-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://hooks.slack.com/services/…"
              className="font-mono text-xs"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name || !url || createMutation.isPending}
            onClick={() =>
              createMutation.mutate({
                moduleKey,
                name,
                url,
              })
            }
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
